import React, { memo, useMemo, useCallback, useState, useEffect } from "react";
import L, { LatLngLiteral } from "leaflet";
import { useMap } from "react-leaflet";
import { react, useChanged } from "uikit";
import objectHash from "object-hash";
import "leaflet-polylinedecorator";

import { Order } from "../../../../../services";
import MapService from "../../../../../services/Map";
import { useTypedSelector } from "../../../../../redux/store";
import PointController from "../../../../PointController";
import { OrderStatus } from "../../../../../types";

import DecoratedPolyline from "./components/DecoratedPolyline";
import LocalController from "./Controller";

const routeColors = [
	"#e83a53",
	"#54ad4a",
	"#397eb0",
	"#974ba4",
	"#f49e3b",
	"#fcfb2e",
	"#ad4f20",
	"#f680bd",
	"#aca7a3",
];

const RouteBase = react.withController(({ controller }) => {
	const language = useTypedSelector((state) => state.session.language);
	const map = useMap();

	const order = useTypedSelector(
		(state) => state.ordersPageReducer.activeOrder as Order.Model,
	);

	const ordersType = useTypedSelector(
		(state) => state.ordersPageReducer.ordersType,
	);

	const [getRoute, setGetRoute] = useState<1 | 2 | 3>(1);
	const [segments, setSegments] = useState<LatLngLiteral[][]>([]);

	const activeOrder = useMemo(() => {
		const orderStatus = order?.status;
		if (
			orderStatus &&
			ordersType === "executable" &&
			orderStatus !== OrderStatus.EXECUTING
		) {
			setSegments([]);
			setGetRoute(3);
			return null;
		}

		if (
			orderStatus &&
			ordersType === "all" &&
			orderStatus === OrderStatus.CLOSED
		) {
			setSegments([]);
			setGetRoute(3);
			return null;
		}

		if (!orderStatus) {
			setSegments([]);
			setGetRoute(3);
			return null;
		}

		return order;
	}, [order, ordersType]);

	const points = useMemo(
		() =>
			(activeOrder?.points as any[])?.map<LatLngLiteral>((point) => ({
				lat: point.coordinates.lat,
				lng: point.coordinates.lng,
			})) ?? [],
		[activeOrder?.points],
	);

	const orderId = useMemo(() => activeOrder?.id, [activeOrder?.id]);
	const segmentLng = useMemo(
		() => activeOrder?.route?.segments?.length || 0,
		[activeOrder?.route?.segments],
	);

	const isTaximeter = useMemo(
		() => activeOrder?.additionalFields?.processedByTaximeter,
		[activeOrder?.additionalFields?.processedByTaximeter],
	);

	const getSegments = useCallback(async () => {
		if (points.length > 1) {
			if (getRoute === 1 && orderId) {
				setGetRoute(3);
				const route = await MapService.getOrderRouteById(orderId);
				if (route?.segments?.length) {
					setSegments(route?.segments ?? []);
					return;
				}
				setGetRoute(2);
			} else if (getRoute === 2 && orderId) {
				const route = await MapService.getSegmentsAndPointsRoute(
					points,
					true,
				);
				if (route?.segments?.length && getRoute === 2) {
					setSegments(route?.segments ?? []);
				}
			}
		} else {
			setSegments([]);
		}
	}, [orderId, points, getRoute]);

	useEffect(() => {
		setGetRoute(1);
	}, [orderId]);

	useEffect(() => {
		if (!activeOrder?.id) {
			setSegments([]);
			setGetRoute(3);
		}
	}, [activeOrder]);

	useEffect(() => {
		if (!isTaximeter && points?.length > 1 && getRoute === 2) {
			const value = points.length - segmentLng;
			if (value > 1 && !isTaximeter) {
				getSegments();
			}
		}
	}, [points?.length, segmentLng, getSegments, isTaximeter, getRoute]);

	useEffect(() => {
		if (
			!isTaximeter &&
			points?.length > 1 &&
			!segments.length &&
			getRoute === 1
		) {
			const value = points.length - segments.length;
			if (value > 1 && !isTaximeter) getSegments();
		}
	}, [points.length, getSegments, isTaximeter, segments.length, getRoute]);

	useEffect(() => {
		if (points?.length > 1 || isTaximeter) {
			const value = activeOrder?.route?.segments ?? [];

			if (!value?.length) {
				getSegments();
			} else {
				setSegments(value);
			}
		} else {
			setSegments([]);
		}
	}, [
		activeOrder?.route?.segments,
		points?.length,
		isTaximeter,
		getSegments,
	]);

	const routePolylines = useMemo(
		() =>
			segments.map((route, index) => (
				<DecoratedPolyline
					key={index}
					positions={route}
					patterns={[
						{
							offset: 25,
							repeat: 90,
							symbol: new L.Symbol.ArrowHead({
								pixelSize: 10,
								pathOptions: {
									color: isTaximeter
										? "#8e00ff"
										: routeColors[
												index % routeColors.length
										  ],
									fillOpacity: 1,
									weight: 0,
								},
							}),
						},
					]}
					pathOptions={{
						color: isTaximeter
							? "#8e00ff"
							: routeColors[index % routeColors.length],
					}}
				/>
			)),
		[segments, isTaximeter],
	);

	const routePoints = useMemo(
		() =>
			points.map((point, index) => (
				<PointController
					key={index}
					value={{ coordinates: point }}
					name={(index + 1).toString()}
					editable={false}
					language={language}
				/>
			)),
		[language, points],
	);

	controller.setContext({ map, routes: segments, points });

	const hash = useMemo(
		() => objectHash({ routes: segments, points }),
		[points, segments],
	);

	useChanged(() => {
		controller.focus();
	}, hash);

	return (
		<>
			{routePolylines}
			{routePoints}
		</>
	);
}, LocalController);

const Route = memo(RouteBase);

declare namespace Route {
	type Controller = LocalController;
}

export default Route;
